import React, { useState, useEffect } from 'react';
import '../BitsStyles/ContactForm.css';
import logo from '../BitsImages/logoooo12345.jpg';

const positions = [
  'Web Developer',
  'Mobile App Developer',
  'UI/UX Designer',
  'Backend Developer',
  'Full Stack Developer',
  'IT Support Engineer',
  'Business Analyst',
  'Software Tester / QA',
  'HR Executive',
  'Sales & Marketing',
];

const applicationSteps = [
  { icon: 'bi-send-fill',        label: 'Submit Application' },
  { icon: 'bi-journal-text',     label: 'Aptitude Round'     },
  { icon: 'bi-code-slash',       label: 'Technical Round'    },
  { icon: 'bi-person-check-fill',label: 'HR Interview'       },
  { icon: 'bi-trophy-fill',      label: 'Offer Letter'       },
];

const JobApplicationForm = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    const els = document.querySelectorAll('[data-animate-job]');
    els.forEach(el => observer.observe(el));
    return () => els.forEach(el => observer.unobserve(el));
  }, []);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    portfolio: '',
    coverLetter: '',
    agreePrivacy: false,
  });

  const [errors, setErrors] = useState({});
  const [result, setResult] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.fullName)    newErrors.fullName    = 'Full name is required';
    if (!formData.email)       newErrors.email       = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone)       newErrors.phone       = 'Phone number is required';
    else if (!/^[6789]\d{9}$/.test(formData.phone)) newErrors.phone = 'Phone must start with 6–9 and be 10 digits';
    if (!formData.position)    newErrors.position    = 'Please select a position';
    if (!formData.coverLetter) newErrors.coverLetter = 'Cover letter / message is required';
    if (!formData.agreePrivacy) newErrors.agreePrivacy = 'You must agree to the privacy policy';

    if (Object.keys(newErrors).length === 0) {
      try {
        setResult('Sending....');
        const payload = new FormData();
        payload.append('access_key', '017080b9-1d21-4076-b2b8-4ea5040869d9');
        payload.append('name',       formData.fullName);
        payload.append('email',      formData.email);
        payload.append('phone',      formData.phone);
        payload.append('position',   formData.position);
        payload.append('experience', formData.experience || 'Not specified');
        payload.append('portfolio',  formData.portfolio  || 'Not provided');
        payload.append('message',    formData.coverLetter);
        payload.append('subject',    `Job Application – ${formData.position}`);

        const response = await fetch('https://api.web3forms.com/submit', { method: 'POST', body: payload });
        const data = await response.json();

        if (data.success) {
          setResult('Application Submitted Successfully! We will get back to you soon.');
          setFormData({ fullName: '', email: '', phone: '', position: '', experience: '', portfolio: '', coverLetter: '', agreePrivacy: false });
        } else {
          setResult(data.message);
        }
      } catch {
        setResult('An error occurred. Please try again.');
      }
    }
    setErrors(newErrors);
  };

  return (
    <section className="bodyofcontact">
      <div className="container">

        {/* ── Application Form Card ── */}
        <div className="contact-form" data-animate-job="true">
          <h1 className="form-title">Apply for a Position</h1>
          <form onSubmit={handleSubmit}>

            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Your full name"
                required
              />
              {errors.fullName && <div className="error">{errors.fullName}</div>}
            </div>

            <div className="form-group">
              <label>Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your email address"
                required
              />
              {errors.email && <div className="error">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label>Phone *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Your phone number"
                required
              />
              {errors.phone && <div className="error">{errors.phone}</div>}
            </div>

            <div className="form-group">
              <label>Position Applying For *</label>
              <select name="position" value={formData.position} onChange={handleChange} required>
                <option value="">Select a position</option>
                {positions.map(pos => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
              {errors.position && <div className="error">{errors.position}</div>}
            </div>

            <div className="form-group">
              <label>Years of Experience</label>
              <select name="experience" value={formData.experience} onChange={handleChange}>
                <option value="">Select experience level</option>
                <option value="fresher">Fresher (0 years)</option>
                <option value="0-1">Less than 1 year</option>
                <option value="1-3">1 – 3 years</option>
                <option value="3-5">3 – 5 years</option>
                <option value="5+">5+ years</option>
              </select>
            </div>

            <div className="form-group">
              <label>LinkedIn / Portfolio URL</label>
              <input
                type="url"
                name="portfolio"
                value={formData.portfolio}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>

            <div className="form-group">
              <label>Cover Letter / Message *</label>
              <textarea
                rows="5"
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleChange}
                placeholder="Tell us why you'd be a great fit at Bellary Info Tech Solutions..."
                style={{ resize: 'none' }}
              ></textarea>
              {errors.coverLetter && <div className="error">{errors.coverLetter}</div>}
            </div>

            <div className="checkbox-group">
              <input
                type="checkbox"
                id="jobPrivacy"
                name="agreePrivacy"
                checked={formData.agreePrivacy}
                onChange={handleChange}
              />
              <label htmlFor="jobPrivacy">
                I agree to provide information in accordance with Bellary Info Tech Solutions privacy policies.
              </label>
              {errors.agreePrivacy && <div className="error">{errors.agreePrivacy}</div>}
            </div>

            <button type="submit" className="submit-btnn">Submit Application</button>
          </form>

          {result && (
            <span style={{
              color: result.includes('Successfully') ? '#4ade80' : '#FCA5A5',
              fontWeight: 600,
              display: 'block',
              marginTop: '1rem',
              textAlign: 'center',
              lineHeight: 1.5,
            }}>
              {result}
            </span>
          )}
        </div>

        {/* ── Info Card ── */}
        <div className="scheduler">
          <div className="profile">
            <img src={logo} alt="BITS Logo" className="profile-img" />
            <h2 className="meeting-title">Join Bellary Info Tech Solutions</h2>
          </div>

          <div className="contact-details" style={{ marginBottom: '1.5rem' }}>
            <div className="contact-item">
              <i className="bi bi-stars contact-icon"></i>
              <span>Work on cutting-edge IT and billing solutions</span>
            </div>
            <div className="contact-item">
              <i className="bi bi-graph-up-arrow contact-icon"></i>
              <span>Fast-track career growth and skill development</span>
            </div>
            <div className="contact-item">
              <i className="bi bi-people-fill contact-icon"></i>
              <span>Collaborative team culture that values your ideas</span>
            </div>
            <div className="contact-item">
              <i className="bi bi-geo-alt-fill contact-icon"></i>
              <a href="https://www.google.com/maps/place/15%C2%B009'42.8%22N+76%C2%B056'18.5%22E/@15.1618988,76.9358962,17z" target="_blank" rel="noopener noreferrer">Plot No 7, Ward No 18/21, Kappagal Road, Bellary 583103</a>
            </div>
            <div className="contact-item">
              <i className="bi bi-envelope-fill contact-icon"></i>
              <a href="mailto:info@bellaryinfotech.com">info@bellaryinfotech.com</a>
            </div>
          </div>

          <h4 style={{ color: 'white', fontWeight: 700, marginBottom: '1rem' }}>
            Application Process
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {applicationSteps.map((step, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <i className={`bi ${step.icon}`} style={{ color: 'white', fontSize: '0.85rem' }}></i>
                </div>
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.875rem', fontWeight: 600 }}>
                  {step.label}
                </span>
                {i < applicationSteps.length - 1 && (
                  <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem' }}>
                    <i className="bi bi-chevron-right"></i>
                  </span>
                )}
              </div>
            ))}
          </div>

          <div className="quote">"We don't just hire employees — we grow careers."</div>
        </div>

      </div>
    </section>
  );
};

export default JobApplicationForm;
